import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CharacterCard from "@/components/base/CharacterCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { characters, characterRaces, characterTags } from "@/mocks/characters";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "韩国妖精漫画角色图鉴",
    description: "韩国妖精漫画女主角色图鉴，按身份与萌娘属性整理女主颜值、人设与CP组合。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://mangami2.github.io/" },
      { "@type": "ListItem", position: 2, name: "角色图鉴", item: "https://mangami2.github.io/encyclopedia" },
    ],
  },
];

export default function Encyclopedia() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const race = searchParams.get("race") ?? "全部";
  const tag = searchParams.get("tag") ?? "全部";

  usePageMeta(
    "韩国妖精漫画角色图鉴｜女主萌娘人设·CP组合大全",
    "韩国妖精漫画女主角色图鉴，收录猫妖、九尾狐、凤凰、冰雪精灵等漂亮女主，按身份与萌娘属性筛选，附CP组合与人物关系，一站查清角色设定。",
    jsonLd
  );

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return characters.filter((c) => {
      if (race !== "全部" && c.race !== race) return false;
      if (tag !== "全部" && !c.tags.some((t) => t === tag) && !c.tags.some((t) => t.includes(tag))) return false;
      if (q) {
        const hay = `${c.name}${c.title}${c.workName}${c.intro}${c.cp}${c.race}${c.tags.join("")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [keyword, race, tag]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParam("q", keyword.trim());
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">角色图鉴</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">韩国妖精漫画角色图鉴</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              按身份与萌娘属性整理韩国妖精漫画的漂亮女主，颜值与人设、CP组合、人物关系全部收录，输入名字即可快速定位。
            </p>

            <form onSubmit={submitSearch} className="mt-6 flex max-w-lg flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索女主名字、属性或作品…"
                  aria-label="搜索女主"
                  className="w-full rounded-full border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-line" aria-hidden="true"></i>
                搜索
              </button>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-sm font-medium text-foreground-700">
              <i className="ri-account-circle-line text-primary-600" aria-hidden="true"></i>
              身份
            </span>
            {characterRaces.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => updateParam("race", r)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  race === r ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {r}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-sm font-medium text-foreground-700">
              <i className="ri-heart-2-line text-accent-600" aria-hidden="true"></i>
              萌娘属性
            </span>
            {characterTags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => updateParam("tag", t)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  tag === t ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
            <p className="text-sm text-foreground-600">
              共 <strong className="text-foreground-950">{filtered.length}</strong> 位女主
              {race !== "全部" && <span className="text-primary-600"> · {race}</span>}
              {tag !== "全部" && <span className="text-accent-600"> · {tag}</span>}
            </p>
            {(race !== "全部" || tag !== "全部" || keyword) && (
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams({}, { replace: true });
                }}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-primary-600"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
                清除筛选
              </button>
            )}
          </div>

          {filtered.length ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {filtered.map((c) => (
                <CharacterCard key={c.slug} character={c} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">没找到匹配的女主</h2>
              <p className="max-w-sm text-sm text-foreground-600">换个身份、萌娘属性或关键词试试，图鉴还在持续收录更多韩国妖精漫画角色。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams({}, { replace: true });
                }}
                className="mt-2 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部角色
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}